import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import { LoadingButton } from "../components/Spinner";
import { AuthShell, AuthLogo, FormError, authQuery, parseErr } from "../components/AuthBits";

// Dedicated forgot-password page. Deliberately minimal: no Google
// button (there's nothing to OAuth into when you're resetting a
// password), just the email field and a clear way back to sign-in.
export default function ForgotPassword() {
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const forgot = useMutation({
    mutationFn: () => api.forgotPassword(email.trim().toLowerCase()),
    onSuccess: () => setSent(true),
    onError: (e) => setErr(parseErr(e, "Something went wrong.")),
  });

  return (
    <AuthShell>
      <div className="text-center space-y-3">
        <AuthLogo />
        <h1 className="text-2xl font-bold tracking-tight">Reset your password.</h1>
        <p className="text-sm muted">
          Enter the email on your account and we'll send a reset link.
        </p>
      </div>

      {sent ? (
        <div className="auth-mode text-sm text-center muted space-y-4">
          <p>If an account exists for <b className="text-text">{email}</b>, a reset link is on its way.</p>
          <Link to={`/login${authQuery(sp)}`} className="hover:underline text-xs">
            Back to <b className="font-semibold text-text">Sign in</b>
          </Link>
        </div>
      ) : (
        <>
          <form
            className="space-y-3"
            onSubmit={(e) => { e.preventDefault(); setErr(null); forgot.mutate(); }}
          >
            <input
              className="input"
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
            />
            <FormError msg={err} />
            <LoadingButton
              type="submit"
              className="btn-primary w-full justify-center"
              pending={forgot.isPending}
              pendingLabel="Sending…"
            >
              Send reset link
            </LoadingButton>
          </form>

          <div className="flex items-center justify-end text-xs muted">
            <Link to={`/login${authQuery(sp)}`} className="hover:underline">
              Back to <b className="font-semibold text-text">Sign in</b>
            </Link>
          </div>
        </>
      )}
    </AuthShell>
  );
}
